import { RepurchaseFlowStepper } from "@fero/components";
import { useIsFeatureEnabled } from "@fero/hooks";
import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";

import { Button, Stack } from "@mui/material";
import type { Country } from "adl-gen/common";
import type { StorageLocationView } from "adl-gen/ferovinum/app/api";
import type { Incoterms, StorageLocationId } from "adl-gen/ferovinum/app/db";
import type { DeliveryOptionReq } from "adl-gen/ferovinum/app/deliveryoptions";
import { useFormik } from "formik";
import { useCallback, useMemo } from "react";
import { formatDateToISO8601, formatDateToLocalTime } from "utils/date-utils";
import { ensure } from "utils/null-and-undefined-utils";
import type { RepurchaseRequest } from "../organisation-repurchase-stock/organisation-repurchase-stock-page";
import {
  AvailableDeliveryOptions,
  createValidationSchema,
  getInitialFormValues,
} from "./components";
import { InternalReferenceCard } from "./components/internal-reference-card";
import type { DeliveryType } from "./organisation-repurchase-delivery-page";

export interface AddressForm {
  deliveryPointName: string;
  streetName: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  postCode: string;
  country: Country | null;
  state: string | null;
  deliveryContact: string;
  deliveryContactEmail: string;
  deliveryContactNumber: string;
  deliveryInstructions: string;
  deliveryDate: Date | null;
  deliveryTimeEarliest: Date | null;
  deliveryTimeLatest: Date | null;
  deliveryIncoterms: Incoterms | null;
  bonded: boolean;
}

export interface DeliveryFormValues {
  selectedType: DeliveryType;
  accountCode: string;
  siteCode: string;
  savedAddress?: AddressForm;
  customerAddress?: AddressForm;
  internalReference?: string;
}

const today = new Date();
today.setHours(0, 0, 0, 0);

export interface OrganisationRepurchaseDeliveryPageViewProps {
  repurchaseRequest: RepurchaseRequest;
  storageLocation: StorageLocationView;
  internalTransferLocations: StorageLocationView[];
  initialDeliveryReq: DeliveryOptionReq;

  onConfirm(
    deliveryReq: DeliveryOptionReq,
    destinationLocationId?: StorageLocationId,
    internalRefrence?: string,
  ): void;

  onBack(): void;
}

export const OrganisationRepurchaseDeliveryPageView = (
  props: OrganisationRepurchaseDeliveryPageViewProps,
) => {
  const { initialDeliveryReq, storageLocation, onConfirm, onBack } = props;
  const showInternalRefrenceCard = useIsFeatureEnabled(
    "sale_order_internal_reference",
  );
  const validationSchema = useMemo(
    () => createValidationSchema(storageLocation, showInternalRefrenceCard),
    [storageLocation, showInternalRefrenceCard],
  );
  const onSubmit = useCallback(
    (formValues: DeliveryFormValues) =>
      handleFormSubmit(
        formValues,
        validationSchema,
        onConfirm,
        props,
        storageLocation,
      ),
    [onConfirm, props, storageLocation, validationSchema],
  );

  const initialValues = useMemo(
    () => getInitialFormValues(initialDeliveryReq, storageLocation),
    [initialDeliveryReq, storageLocation],
  );

  const form = useFormik<DeliveryFormValues>({
    initialValues,
    validationSchema,
    onSubmit,
  });

  return (
    <PortalPageContent header={<OrganisationRepurchaseDeliveryStockHeader />}>
      <Stack spacing={7} justifyContent="space-between">
        <AvailableDeliveryOptions form={form} {...props} />
        {showInternalRefrenceCard && <InternalReferenceCard form={form} />}
        <Stack direction="row" spacing={2} justifyContent={"right"}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Button onClick={() => form.submitForm()}>Next</Button>
        </Stack>
      </Stack>
    </PortalPageContent>
  );
};

const OrganisationRepurchaseDeliveryStockHeader = () => {
  return (
    <PortalPageContentHeader
      variant="split"
      title="Repurchase Stock"
      right={<RepurchaseFlowStepper activeStep={1} />}
    />
  );
};

const handleFormSubmit = (
  formValues: DeliveryFormValues,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema: any,
  onConfirm: OrganisationRepurchaseDeliveryPageViewProps["onConfirm"],
  props: OrganisationRepurchaseDeliveryPageViewProps,
  storageLocation: StorageLocationView,
) => {
  const values = validationSchema.cast(
    formValues,
  ) as unknown as DeliveryFormValues;
  let destinationLocationId: string | undefined;
  let request: DeliveryOptionReq = {
    kind: "inWarehouseTransfer",
    value: {},
  };
  switch (values.selectedType) {
    case "warehouse":
      if (values.accountCode) {
        request = {
          kind: "inWarehouseTransferWithAccountCode",
          value: {
            siteCode: values.siteCode ?? null,
            accountCode: values.accountCode,
          },
        };
      }
      if (values.siteCode && values.siteCode !== storageLocation.siteCode) {
        destinationLocationId =
          props.internalTransferLocations.find(
            (loc) => loc.siteCode === values.siteCode,
          )?.id ?? storageLocation.id;
      } else {
        destinationLocationId = storageLocation.id;
      }
      break;
    case "savedDeliveryLocation":
    case "customerLocation": {
      const address =
        values.selectedType === "savedDeliveryLocation"
          ? ensure(values.savedAddress)
          : ensure(values.customerAddress);
      request = {
        kind:
          values.selectedType === "savedDeliveryLocation"
            ? "orgLocationDelivery"
            : "shippedDelivery",
        value: {
          ...address,
          deliveryDate: address.deliveryDate
            ? formatDateToISO8601(address.deliveryDate)
            : null,
          deliveryTimeEarliest: address.deliveryTimeEarliest
            ? formatDateToLocalTime(address.deliveryTimeEarliest)
            : null,
          deliveryTimeLatest: address.deliveryTimeLatest
            ? formatDateToLocalTime(address.deliveryTimeLatest)
            : null,
        },
      };
    }
  }
  onConfirm(request, destinationLocationId, values.internalReference);
};
